import { Link } from 'react-router-dom';
import { useState } from 'react';
import { 
  ArrowRight, CheckCircle, Clock, Users, Award, BookOpen, 
  Wrench, Cog, Building2, Briefcase, GraduationCap, Play,
  Monitor, FileText, Settings, Globe, Shield, TrendingUp, Database
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { createLead } from '@/lib/api';

// Oracle Cloud Certification Training Paths
const trainingPaths = [
  {
    id: 'financials',
    name: 'Oracle Cloud Financials',
    level: 'Beginner to Advanced',
    duration: '10-12 weeks',
    description: 'Master Oracle Fusion Cloud Financials including General Ledger, Accounts Payable, Accounts Receivable, Fixed Assets, and Cash Management for enterprise financial operations.',
    color: 'from-red-500 to-red-700',
    icon: '💰',
    topics: [
      'Oracle Cloud Infrastructure Basics',
      'General Ledger Configuration',
      'Accounts Payable & Receivable',
      'Fixed Assets Management',
      'Cash & Bank Management',
      'Intercompany Accounting',
      'Financial Reporting Studio',
      'Period Close & Consolidation'
    ],
    outcomes: [
      'Configure Oracle Cloud Financials modules',
      'Implement end-to-end financial processes',
      'Create OTBI reports and dashboards',
      'Manage multi-org financial structures',
      'Prepare for Oracle Financials Cloud certification'
    ],
    salary: '$90,000 - $140,000',
    demand: 'Very High',
    industries: ['Financial Services', 'Healthcare', 'Public Sector', 'Technology']
  },
  {
    id: 'scm',
    name: 'Oracle Cloud SCM',
    level: 'Beginner to Advanced',
    duration: '10-12 weeks',
    description: 'Become an expert in Oracle Fusion Cloud Supply Chain Management covering Procurement, Inventory, Order Management, and Manufacturing.',
    color: 'from-orange-500 to-orange-700',
    icon: '📦',
    topics: [
      'Procurement Cloud Configuration',
      'Supplier Management',
      'Inventory Management',
      'Order Management',
      'Product Information Management',
      'Manufacturing Cloud',
      'Logistics & Shipping',
      'SCM Analytics'
    ],
    outcomes: [
      'Configure Oracle SCM Cloud modules',
      'Implement procurement workflows',
      'Manage inventory and warehouses',
      'Set up order-to-cash processes',
      'Prepare for Oracle SCM Cloud certification'
    ],
    salary: '$95,000 - $145,000',
    demand: 'Very High',
    industries: ['Manufacturing', 'Retail', 'Distribution', 'Consumer Goods']
  },
  {
    id: 'hcm',
    name: 'Oracle Cloud HCM',
    level: 'Beginner to Advanced',
    duration: '10-12 weeks',
    description: 'Master Oracle Fusion Cloud Human Capital Management for Core HR, Talent Management, Workforce Management, and Payroll.',
    color: 'from-blue-500 to-blue-700',
    icon: '👥',
    topics: [
      'Core HR & Global HR',
      'Workforce Structures',
      'Talent Acquisition',
      'Performance Management',
      'Learning & Development',
      'Compensation & Benefits',
      'Payroll & Absence Management',
      'HCM Analytics'
    ],
    outcomes: [
      'Configure Oracle HCM Cloud modules',
      'Implement talent management processes',
      'Set up payroll and benefits',
      'Create workforce analytics',
      'Prepare for Oracle HCM Cloud certification'
    ],
    salary: '$85,000 - $135,000',
    demand: 'High',
    industries: ['All Industries', 'Professional Services', 'Healthcare']
  },
  {
    id: 'epm',
    name: 'Oracle Cloud EPM',
    level: 'Intermediate to Advanced',
    duration: '8-10 weeks',
    description: 'Specialize in Oracle Enterprise Performance Management for Planning, Budgeting, Forecasting, and Financial Consolidation.',
    color: 'from-purple-500 to-purple-700',
    icon: '📊',
    topics: [
      'EPM Cloud Overview',
      'Planning & Budgeting',
      'Financial Consolidation (FCCS)',
      'Account Reconciliation',
      'Profitability & Cost Management',
      'Enterprise Data Management',
      'Narrative Reporting',
      'EPM Integrations'
    ],
    outcomes: [
      'Configure Oracle EPM Cloud applications',
      'Build planning models and forms',
      'Implement consolidation rules',
      'Create management reports',
      'Prepare for Oracle EPM Cloud certification'
    ],
    salary: '$100,000 - $150,000',
    demand: 'High',
    industries: ['Financial Services', 'Manufacturing', 'Retail', 'Technology']
  }
];

// Oracle Modules
const oracleModules = [
  { id: 'gl', name: 'General Ledger', icon: FileText, duration: '2 weeks', topics: ['Chart of Accounts', 'Ledger Setup', 'Journal Entries', 'Allocations', 'Revaluations'] },
  { id: 'ap', name: 'Accounts Payable', icon: TrendingUp, duration: '2 weeks', topics: ['Supplier Setup', 'Invoice Processing', 'Payments', 'Holds & Approvals', 'Reporting'] },
  { id: 'ar', name: 'Accounts Receivable', icon: Building2, duration: '2 weeks', topics: ['Customer Setup', 'Invoicing', 'Receipts', 'Collections', 'Revenue Recognition'] },
  { id: 'procurement', name: 'Procurement Cloud', icon: Globe, duration: '2 weeks', topics: ['Requisitions', 'Purchase Orders', 'Sourcing', 'Contracts', 'Supplier Portal'] },
  { id: 'inventory', name: 'Inventory Management', icon: Cog, duration: '2 weeks', topics: ['Item Setup', 'Organizations', 'Transactions', 'Costing', 'Cycle Counting'] },
  { id: 'reporting', name: 'OTBI & Reporting', icon: Monitor, duration: '1.5 weeks', topics: ['OTBI Reports', 'Financial Reporting', 'Smart View', 'BI Publisher', 'Dashboards'] }
];

// Corporate clients
const corporateClients = ['Bank of America', 'FedEx', 'AT&T', 'Cisco', 'LinkedIn', 'Verizon'];

// Industries
const industries = [
  { name: 'Financial Services', icon: TrendingUp },
  { name: 'Healthcare', icon: Shield },
  { name: 'Public Sector', icon: Building2 },
  { name: 'Retail', icon: Globe },
  { name: 'Technology', icon: Monitor },
  { name: 'Manufacturing', icon: Cog }
];

export default function OracleDetailPage() {
  const [selectedPath, setSelectedPath] = useState('financials');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const handleDemoRequest = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await createLead({
        first_name: 'Oracle Demo',
        last_name: 'Request',
        email,
        message: 'Interested in Oracle Cloud ERP Training - Demo Request',
        source: 'oracle_demo'
      });
      toast.success('Demo request submitted! We\'ll contact you within 24 hours.');
      setEmail('');
    } catch (error) {
      toast.error('Failed to submit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-24" data-testid="oracle-detail-page">
      {/* Hero Section */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-red-50 via-white to-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Link to="/programs" className="inline-flex items-center text-sm text-red-600 hover:text-red-700 mb-6">
                <ArrowRight className="w-4 h-4 mr-1 rotate-180" />
                Back to Programs
              </Link>
              
              <Badge className="bg-red-100 text-red-700 mb-4">Enterprise-Grade Cloud Solutions</Badge>
              
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center text-3xl mb-6">
                ☁️
              </div>
              
              <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4">
                Oracle Cloud ERP Training
              </h1>
              <p className="text-xl text-red-600 font-medium mb-4">
                Oracle Fusion Cloud Applications
              </p>
              <p className="text-lg text-slate-600 mb-8">
                Oracle Cloud ERP is a complete, innovative, and proven solution for organizations of all sizes. 
                Master Oracle Fusion Cloud Applications including Financials, SCM, HCM, and EPM to become a 
                sought-after Oracle Cloud consultant.
              </p>
              
              <div className="flex flex-wrap gap-4 mb-8">
                <Link to="/pricing">
                  <Button className="bg-red-600 hover:bg-red-700 text-white rounded-full px-6" data-testid="oracle-enroll-btn">
                    Enroll Now <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/webinar">
                  <Button variant="outline" className="rounded-full px-6 border-red-200 text-red-700 hover:bg-red-50">
                    <Play className="w-4 h-4 mr-2" /> Free Demo Session
                  </Button>
                </Link>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                  <p className="text-2xl font-bold text-red-600">430K+</p>
                  <p className="text-xs text-slate-500">Cloud Customers</p>
                </div>
                <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                  <p className="text-2xl font-bold text-red-600">$150K+</p>
                  <p className="text-xs text-slate-500">Avg Salary</p>
                </div>
                <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                  <p className="text-2xl font-bold text-red-600">94%</p>
                  <p className="text-xs text-slate-500">Placement Rate</p>
                </div>
              </div>
            </div>
            
            {/* Program Highlights Card */}
            <Card className="shadow-xl border-0 bg-white">
              <CardHeader className="bg-gradient-to-r from-red-600 to-red-700 text-white rounded-t-lg">
                <CardTitle className="flex items-center">
                  <Award className="w-5 h-5 mr-2" />
                  Program Highlights
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                    <Clock className="w-5 h-5 text-red-700" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Flexible Duration</p>
                    <p className="text-sm text-slate-600">8-12 weeks based on track</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                    <Monitor className="w-5 h-5 text-red-700" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Live Online Training</p>
                    <p className="text-sm text-slate-600">Expert-led via Zoom/Teams</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                    <Database className="w-5 h-5 text-red-700" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Hands-On Labs</p>
                    <p className="text-sm text-slate-600">Oracle Cloud instance access</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                    <GraduationCap className="w-5 h-5 text-red-700" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">OCI Certification Prep</p>
                    <p className="text-sm text-slate-600">Oracle certification guidance</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                    <Briefcase className="w-5 h-5 text-red-700" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Job Assistance</p>
                    <p className="text-sm text-slate-600">Resume & interview prep</p>
                  </div>
                </div>
                
                {/* Demo Request Form */}
                <div className="pt-4 border-t border-slate-100">
                  <p className="text-sm font-medium text-slate-700 mb-2">Request Free Demo</p>
                  <form onSubmit={handleDemoRequest} className="flex gap-2">
                    <Input 
                      type="email" 
                      placeholder="Enter your email" 
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="flex-1"
                      data-testid="oracle-demo-email"
                    />
                    <Button type="submit" className="bg-red-600 hover:bg-red-700" disabled={loading}>
                      {loading ? '...' : 'Go'}
                    </Button>
                  </form>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-12 bg-white border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-slate-500 mb-6 font-medium">Industries Using Oracle Cloud</p>
          <div className="flex flex-wrap justify-center gap-4">
            {industries.map((industry) => (
              <div key={industry.name} className="flex items-center px-4 py-2 bg-slate-50 rounded-full text-sm">
                <industry.icon className="w-4 h-4 text-red-600 mr-2" />
                <span className="text-slate-700">{industry.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Oracle Cloud Certification Paths */}
      <section className="py-20 lg:py-28" id="certifications">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="bg-red-100 text-red-700 mb-4">Official Oracle Cloud Certifications</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">Choose Your Oracle Cloud Certification Path</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Enroll in specific Oracle Fusion Cloud certification training aligned with your career goals
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {trainingPaths.map((path) => (
              <Card 
                key={path.id} 
                className={`relative overflow-hidden border-2 transition-all duration-300 cursor-pointer ${
                  selectedPath === path.id ? 'border-red-500 shadow-xl ring-2 ring-red-200' : 'border-slate-200 hover:border-red-300 hover:shadow-lg'
                }`}
                onClick={() => setSelectedPath(path.id)}
                data-testid={`cert-path-${path.id}`}
              >
                <div className={`h-2 bg-gradient-to-r ${path.color}`} />
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <span className="text-3xl">{path.icon}</span>
                      <div>
                        <CardTitle className="text-xl text-slate-900">{path.name}</CardTitle>
                        <p className="text-sm text-slate-500">{path.level} • {path.duration}</p>
                      </div>
                    </div>
                    <Badge className={`${path.demand === 'Very High' ? 'bg-green-500 text-white' : 'bg-green-100 text-green-700'}`}>
                      {path.demand} Demand
                    </Badge>
                  </div>
                  <CardDescription className="text-slate-600">{path.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {/* Industries */}
                    <div className="flex flex-wrap gap-2">
                      {path.industries.map((ind, i) => (
                        <span key={i} className="text-xs px-2 py-1 bg-slate-100 text-slate-600 rounded-full">{ind}</span>
                      ))}
                    </div>
                    
                    {/* Salary */}
                    <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                      <span className="text-sm text-slate-600">Expected Salary:</span>
                      <span className="font-bold text-green-700">{path.salary}</span>
                    </div>
                    
                    {/* Topics */}
                    <div>
                      <p className="text-sm font-medium text-slate-700 mb-2">Key Topics:</p>
                      <div className="grid grid-cols-2 gap-1">
                        {path.topics.slice(0, 6).map((topic, i) => (
                          <div key={i} className="flex items-center text-xs text-slate-600">
                            <CheckCircle className="w-3 h-3 text-red-500 mr-1 flex-shrink-0" />
                            <span className="truncate">{topic}</span>
                          </div>
                        ))}
                      </div>
                      {path.topics.length > 6 && (
                        <p className="text-xs text-red-600 mt-1">+{path.topics.length - 6} more topics</p>
                      )}
                    </div>
                    
                    {/* CTA */}
                    <div className="flex gap-3 pt-2">
                      <Link to="/pricing" className="flex-1">
                        <Button className={`w-full bg-gradient-to-r ${path.color} text-white hover:opacity-90`}>
                          Enroll Now
                        </Button>
                      </Link>
                      <Link to="/webinar">
                        <Button variant="outline" className="border-slate-300">
                          <Play className="w-4 h-4" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Training Curriculum */}
      <section className="py-20 lg:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">Oracle Cloud Training Curriculum</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Comprehensive modules covering Oracle Fusion Cloud applications
            </p>
          </div>
          
          <Tabs defaultValue="gl" className="w-full">
            <TabsList className="flex flex-wrap justify-center gap-2 mb-10 bg-transparent">
              {oracleModules.slice(0, 4).map((module) => (
                <TabsTrigger 
                  key={module.id} 
                  value={module.id}
                  className="px-4 py-2 rounded-full border border-slate-200 data-[state=active]:border-red-500 data-[state=active]:bg-red-50 data-[state=active]:text-red-700"
                >
                  {module.name}
                </TabsTrigger>
              ))}
            </TabsList>

            {oracleModules.map((module) => (
              <TabsContent key={module.id} value={module.id}>
                <Card className="max-w-4xl mx-auto">
                  <CardContent className="p-8">
                    <div className="flex items-start space-x-4 mb-6">
                      <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                        <module.icon className="w-6 h-6 text-red-700" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-slate-900">{module.name}</h3>
                        <p className="text-red-600 font-medium">Duration: {module.duration}</p>
                      </div>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-semibold text-slate-900 mb-4">Topics Covered</h4>
                        <ul className="space-y-3">
                          {module.topics.map((topic, i) => (
                            <li key={i} className="flex items-start space-x-2">
                              <CheckCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                              <span className="text-slate-600">{topic}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-900 mb-4">What You'll Learn</h4>
                        <ul className="space-y-3">
                          <li className="flex items-start space-x-2">
                            <BookOpen className="w-5 h-5 text-red-500 mt-0.5" />
                            <span className="text-slate-600">Real-time scenario-based training</span>
                          </li>
                          <li className="flex items-start space-x-2">
                            <Monitor className="w-5 h-5 text-red-500 mt-0.5" />
                            <span className="text-slate-600">Hands-on lab exercises</span>
                          </li>
                          <li className="flex items-start space-x-2">
                            <FileText className="w-5 h-5 text-red-500 mt-0.5" />
                            <span className="text-slate-600">Study materials & documentation</span>
                          </li>
                          <li className="flex items-start space-x-2">
                            <Award className="w-5 h-5 text-red-500 mt-0.5" />
                            <span className="text-slate-600">Certification guidance</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            ))}
          </Tabs>

          {/* All Modules Grid */}
          <div className="mt-12">
            <h3 className="text-xl font-bold text-slate-900 mb-6 text-center">All Oracle Cloud Modules</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
              {oracleModules.map((module, index) => (
                <Card key={module.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                        <span className="text-red-700 font-mono font-bold text-sm">{String(index + 1).padStart(2, '0')}</span>
                      </div>
                      <div>
                        <p className="font-medium text-slate-900 text-sm">{module.name}</p>
                        <p className="text-xs text-slate-500">{module.duration}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Career Outcomes */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-6">Oracle Cloud Certifications We Prepare You For</h2>
              <p className="text-slate-600 mb-6">
                Our training programs prepare you for official Oracle Cloud certifications.
              </p>
              <ul className="space-y-3">
                {[
                  { cert: 'Oracle Financials Cloud Certified Implementation Specialist', color: 'text-red-600' },
                  { cert: 'Oracle SCM Cloud Certified Implementation Specialist', color: 'text-orange-600' },
                  { cert: 'Oracle HCM Cloud Certified Implementation Specialist', color: 'text-blue-600' },
                  { cert: 'Oracle EPM Cloud Certified Implementation Specialist', color: 'text-purple-600' },
                  { cert: 'Oracle Cloud Infrastructure (OCI) Foundations', color: 'text-slate-600' }
                ].map((item, i) => (
                  <li key={i} className="flex items-start space-x-2">
                    <Award className={`w-5 h-5 ${item.color} mt-0.5`} />
                    <span className="text-slate-700 font-medium">{item.cert}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-6">Career Opportunities</h2>
              <p className="text-slate-600 mb-6">
                Oracle Cloud skills command premium salaries. Here are roles you can pursue.
              </p>
              <ul className="space-y-3">
                {[
                  'Oracle Cloud Financials Consultant',
                  'Oracle SCM Functional Consultant',
                  'Oracle HCM Implementation Specialist',
                  'Oracle EPM Consultant',
                  'Oracle Cloud Solution Architect',
                  'Oracle Cloud Technical Consultant',
                  'Oracle Integration Specialist',
                  'Oracle Cloud Project Manager'
                ].map((role, i) => (
                  <li key={i} className="flex items-start space-x-2">
                    <Briefcase className="w-5 h-5 text-red-500 mt-0.5" />
                    <span className="text-slate-700">{role}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Corporate Clients */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-slate-500 mb-6 font-medium">Companies Using Oracle Cloud</p>
          <div className="flex flex-wrap justify-center gap-6">
            {corporateClients.map((client) => (
              <div key={client} className="px-6 py-3 bg-white rounded-lg shadow-sm text-slate-700 font-medium">
                {client}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-gradient-to-r from-red-600 to-red-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              Ready to Master Oracle Cloud ERP?
            </h2>
            <p className="text-lg text-red-100 mb-8">
              Join our next batch and become a certified Oracle Cloud professional. 
              Customized training available for individuals and corporate teams.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/pricing">
                <Button className="bg-white text-red-700 hover:bg-red-50 rounded-full px-8 py-3 font-medium">
                  View Pricing & Enroll
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="border-white text-white hover:bg-white/10 rounded-full px-8 py-3 font-medium">
                  Request Corporate Training
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
